import { useState, useEffect, useCallback } from 'react';
import axiosInstance, { extractErrorMessage } from '../../../services/axiosConfig';

/**
 * Interface para dados de uso diário
 */
export interface DailyUsage {
  day: string; // Abreviação do dia (Seg, Ter, etc.)
  value: number; // Tokens usados no dia
  label: string; // Nome completo do dia (Segunda, Terça, etc.)
  date?: string; // Data ISO (opcional)
}

/**
 * Interface para dados de uso semanal
 */
export interface WeeklyUsageData {
  currentWeek: DailyUsage[];
  previousWeekTotal: number;
  currentWeekTotal: number;
  percentageChange: number;
}

/**
 * Hook para buscar dados de uso semanal de tokens
 *
 * Busca uso de tokens por dia da semana e compara com semana anterior
 *
 * @returns Estado e funções para gerenciar uso semanal
 *
 * @example
 * ```tsx
 * const { weeklyData, loading, error } = useWeeklyUsage();
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage>{error}</ErrorMessage>;
 *
 * return <div>Total: {weeklyData?.currentWeekTotal}</div>;
 * ```
 */
export const useWeeklyUsage = () => {
  const [weeklyData, setWeeklyData] = useState<WeeklyUsageData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWeeklyUsage = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get('/users/usage/weekly');

      // Extract data from BaseResponse structure
      if (response.data?.success && response.data?.data) {
        setWeeklyData(response.data.data);
      } else {
        throw new Error('Formato de resposta inválido');
      }
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Não foi possível carregar os dados de uso semanal'
      );
      setError(errorMessage);
      console.error('Error fetching weekly usage:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWeeklyUsage();
  }, [fetchWeeklyUsage]);

  return {
    weeklyData,
    loading,
    error,
    refetch: fetchWeeklyUsage,
  };
};
